/**
 * UserApp ist der Einstiegspunkt für die User. Die Module werden unterschieden und die Navigation wird bereit gestellt.
 */

import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router'
import { Redirect, Route, Switch } from 'react-router-dom'

import { Container, Grid } from '@mui/material'

import UserDashboard from './UserDashboard/UserDashboard'
import UserFacebookSettings from './UserFacebookSettings/UserFacebookSettings'
import UserStatistics from './UserStatistics/UserStatistics'
import UserTopics from './UserTopics/UserTopics'
import UserHelp from './UserHelp/UserHelp'

import {
  getIgPageInitiated,
  getLinkedInPageInitiated,
  loadFacebookPageInitiated
} from '../../store/dashboard/dashboard.actions'

import { Navigation, UserScreen } from '@professocial/professocialTypes/types/commonTypes'
import { GetFacebookPageRequest, GetInstagramPageRequest } from '@3m5/sm_publish-frontend-api'
import {
  getConnectedFacebookPage,
  getConnectedInstagramPage,
  getConnectedLinkedInPage,
  getTokenChanged,
  getUserPages
} from '../../store/common/common.selectors'
import {
  setConnectedFacebookPage,
  setConnectedInstagramPage,
  setConnectedLinkedInPage
} from '../../store/common/common.actions'
import { NetworkType } from '@professocialApi/professocial-core-frontend-api'
import { GetLinkedInPageRequest } from '@3m5/sm_publish-frontend-api'

export enum UserDashboardSubScreen {
  networkContent = 'networkContent',
  posting = 'posting',
  calendar = 'calendar',
}

export enum UserFacebookSubScreen {
  images = 'images',
  pageInfo = 'pageInfo',
  imprint = 'imprint',
}

export enum UserTopicsSubScreen {
  topics = 'topics',
}

export enum UserStatisticSubScreen {
  facebook = 'facebook',
  instagram = 'instagram',
  googleMyBusiness = 'googleMyBusiness',
}

export const navigationUserListData: Navigation[] = [
  {
    value: 'dashboard',
    label: 'dashboard',
    key: 'dashboard',
    changeTo: UserScreen.dashboard,
  },
  {
    value: 'statistics',
    label: 'statistics',
    key: 'statistics',
    changeTo: UserScreen.statistics,
  },
  {
    value: 'facebook',
    label: 'facebook',
    key: 'facebook',
    changeTo: UserScreen.facebook,
  },
  {
    value: 'topics',
    label: 'topics',
    key: 'topics',
    changeTo: UserScreen.topics,
  },
  {
    value: 'help',
    label: 'help',
    key: 'help',
    changeTo: UserScreen.help,
  },
]

const UserApp: React.FC = () => {
  const dispatch = useDispatch()
  const { path } = useRouteMatch()

  const dispatchGetFacebookPage = useCallback((params: GetFacebookPageRequest) => dispatch(loadFacebookPageInitiated(params)), [dispatch])
  const dispatchGetInstagramPage = useCallback((params: GetInstagramPageRequest) => dispatch(getIgPageInitiated(params)), [dispatch])
  const dispatchGetLinkedInPage = useCallback((params: GetLinkedInPageRequest) => dispatch(getLinkedInPageInitiated(params)), [dispatch])
  const dispatchSetConnectedFacebookPage = useCallback((params: string) => dispatch(setConnectedFacebookPage(params)), [dispatch])
  const dispatchSetConnectedInstagramPage = useCallback((params: string) => dispatch(setConnectedInstagramPage(params)), [dispatch])
  const dispatchSetConnectedLinkedInPage = useCallback((params: string) => dispatch(setConnectedLinkedInPage(params)), [dispatch])

  const connectedFacebookPage: string = useSelector(getConnectedFacebookPage)
  const connectedInstagramPage: string = useSelector(getConnectedInstagramPage)
  const connectedLinkedInPage: string = useSelector(getConnectedLinkedInPage)
  const connectedUserPages = useSelector(getUserPages)
  const tokenChanged = useSelector(getTokenChanged)

  useEffect(() => {
    if (connectedInstagramPage && tokenChanged && !!connectedUserPages?.length) {
      dispatchGetInstagramPage({
        networkPageId: connectedInstagramPage,
      })
    }

    if (connectedFacebookPage && tokenChanged && !!connectedUserPages?.length) {
      dispatchGetFacebookPage({
        networkPageId: connectedFacebookPage,
      })
    }

    if (connectedLinkedInPage) {
      dispatchGetLinkedInPage({
        networkPageId: connectedLinkedInPage,
      })
    }
  }, [connectedFacebookPage, connectedInstagramPage, tokenChanged, connectedUserPages],
  )

  useEffect(() => {
    !!connectedUserPages && !!connectedUserPages.length && connectedUserPages.map(connectedPage => {
      connectedPage.networkType === NetworkType.Facebook && dispatchSetConnectedFacebookPage(connectedPage.networkPageId)
      connectedPage.networkType === NetworkType.Instagram && dispatchSetConnectedInstagramPage(connectedPage.networkPageId)
      connectedPage.networkType === NetworkType.LinkedIn && dispatchSetConnectedLinkedInPage(connectedPage.networkPageId)
    })
  }, [connectedUserPages])

  return (
    <>
      <Container maxWidth='lg'>
        <Switch>
          <Redirect exact from={`${path}`} to={`${path}/dashboard/networkContent`} />
          <Route path={`${path}/dashboard`}>
            <UserDashboard />
          </Route>
          <Route path={`${path}/facebook`}>
            <UserFacebookSettings />
          </Route>
          <Route path={`${path}/statistics`}>
            <UserStatistics />
          </Route>
          <Route path={`${path}/topics`}>
            <Grid container spacing={2} sx={{ px: 2, pb: 12, mt: 1 }}>
              <Grid item xs={12}>
                <UserTopics />
              </Grid>
            </Grid>
          </Route>
          <Route path={`${path}/help`}>
            <UserHelp />
          </Route>
        </Switch>
      </Container>
    </>
  )
}
export default UserApp
